import { useAtomValue } from "jotai"
import { audioVolumeAtom, backdoLaunchAtom, clientAtom, gamePhaseAtom, hostAtom, languageAtom, minigameRuleAtom, nakAtom, shortcutOptionsAtom, timerAtom, yutMoCatchAtom, deviceAtom } from "../GlobalState"
import { useEffect, useState } from "react"
import { getSocket } from "../socket.js"

const socket = getSocket();
import { useParams } from "wouter"
import { useSpring, animated } from "@react-spring/three"
import { MeshDistortMaterial, Text3D } from '@react-three/drei'
import MeshColors from "./MeshColors"
import useSoundEffectsPlayer from "../soundPlayers/useSoundEffectsPlayer"
import translations from "../dictionaries/translations"
import { BackToMainMenuButton } from "./lobby/BackToMainMenu.jsx";
import layout from "../dictionaries/layout"
import { trackEvent } from '../api'

export default function LobbySettings({ position=[0,0,0], scale=1, ruleWidth=7.1, showMainMenu=true }) {
  const device = useAtomValue(deviceAtom)
  const host = useAtomValue(hostAtom)
  const client = useAtomValue(clientAtom)
  const gamePhase = useAtomValue(gamePhaseAtom)
  const isHost = client.socketId === host.socketId
  const params = useParams()
  const { playSoundEffect } = useSoundEffectsPlayer()
  const language = useAtomValue(languageAtom)
  const ruleCardCenter = ruleWidth / 2 - 0.3
  const ruleCardInnerWidth = ruleWidth - 0.05
  const ruleToggleX = ruleWidth - 1.15
  const ruleTextElevation = 0.11
  const titleSize = 0.35
  const descriptionSize = 0.25

  // toggle states (global)
  // pointer handlers (enter, leave, and up for each rule)
  const backdoLaunch = useAtomValue(backdoLaunchAtom)
  const timer = useAtomValue(timerAtom)
  const nak = useAtomValue(nakAtom)
  const yutMoCatch = useAtomValue(yutMoCatchAtom)
  const shortcutOptions = useAtomValue(shortcutOptionsAtom)
  const minigameRule = useAtomValue(minigameRuleAtom)

  function trackRuleToggle(rule, enabled) {
    trackEvent({ surface: 'lobby', action: 'toggle_rule', category: 'ui', metadata: { rule, enabled } })
  }

  const { 
    setting0TogglePosition, 
    setting0ToggleBackgroundColor,
    setting1TogglePosition, 
    setting1ToggleBackgroundColor,
    setting2TogglePosition, 
    setting2ToggleBackgroundColor,
    setting3TogglePosition, 
    setting3ToggleBackgroundColor,
    setting4TogglePosition, 
    setting4ToggleBackgroundColor,
    minigameTogglePosition,
    minigameToggleBackgroundColor,
  } = useSpring({
    setting0TogglePosition: !backdoLaunch ? [0,0,0] : [0.4, 0, 0],
    setting0ToggleBackgroundColor: !isHost ? '#454200' : (!backdoLaunch) ? '#5C5800' : 'green',
    setting1TogglePosition: !timer ? [0,0,0] : [0.4, 0, 0],
    setting1ToggleBackgroundColor: !isHost ? '#454200' : (!timer) ? '#5C5800' : 'green',
    setting2TogglePosition: !nak ? [0,0,0] : [0.4, 0, 0],
    setting2ToggleBackgroundColor: !isHost ? '#454200' : (!nak) ? '#5C5800' : 'green',
    setting3TogglePosition: !yutMoCatch ? [0,0,0] : [0.4, 0, 0],
    setting3ToggleBackgroundColor: !isHost ? '#454200' : (!yutMoCatch) ? '#5C5800' : 'green',
    setting4TogglePosition: !shortcutOptions ? [0,0,0] : [0.4, 0, 0],
    setting4ToggleBackgroundColor: !isHost ? '#454200' : (!shortcutOptions) ? '#5C5800' : 'green',
    minigameTogglePosition: !minigameRule ? [0,0,0] : [0.4, 0, 0],
    minigameToggleBackgroundColor: !isHost ? '#454200' : (!minigameRule) ? '#5C5800' : 'green',
    config: {
      tension: 170,
      friction: 26
    },
  })
  
  const AnimatedMeshDistortMaterial = animated(MeshDistortMaterial)

  function BackdoLaunch({ position }) {
    const audioVolume = useAtomValue(audioVolumeAtom)
    const [setting0Hover, setSetting0Hover] = useState(false)
    
    function handleSetting0PointerEnter(e) {
      e.stopPropagation()
      if (isHost && gamePhase === 'lobby') {
        document.body.style.cursor = 'pointer'
        setSetting0Hover(true)
      }
    }
    function handleSetting0PointerLeave(e) {
      e.stopPropagation()
      if (isHost && gamePhase === 'lobby') {
        document.body.style.cursor = 'default'
        setSetting0Hover(false)
      }
    }
    function handleSetting0PointerDown(e) {
      e.stopPropagation()
      if (isHost && gamePhase === 'lobby') {
        trackRuleToggle('backdo_launch', !backdoLaunch)
        if (!backdoLaunch) {
          socket.emit('setGameRule', ({ roomId: params.id.toUpperCase(), clientId: client._id, rule: 'backdoLaunch', flag: true }))
        } else {
          socket.emit('setGameRule', ({ roomId: params.id.toUpperCase(), clientId: client._id, rule: 'backdoLaunch', flag: false }))
        }
      }
      playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
    }
    
    return <group name='setting-0' position={position}>
      <group name='setting-0-background'>
        { isHost && gamePhase === 'lobby' && <mesh
        name='setting-0-background-outer'
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.05, 2]}>
          <boxGeometry args={[ruleWidth, 1, 1]}/>
          <meshStandardMaterial color='orange'/>
        </mesh> }
        <mesh 
        name='setting-0-background-inner'
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.1, 1.95]}>
          <boxGeometry args={[ruleCardInnerWidth, 1, 1]}/>
          <meshStandardMaterial 
          color={ !setting0Hover ? MeshColors.disabledGreyBackground : '#444444' } 
          transparent 
          opacity={1}/>
        </mesh>
        <mesh 
        name='setting-0-background-wrapper'
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.05, 2]}
        onPointerEnter={e=>handleSetting0PointerEnter(e)}
        onPointerLeave={e=>handleSetting0PointerLeave(e)}
        onPointerDown={e=>handleSetting0PointerDown(e)}>
          <boxGeometry args={[ruleWidth, 1, 1]}/>
          <meshStandardMaterial 
          transparent 
          opacity={0}/>
        </mesh>
      </group>
      <Text3D 
      name='setting-0-title'
      font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
      position={[-0.1,0.1,-0.35]}
      rotation={[-Math.PI/2,0,0]}
      size={titleSize}
      height={0.01}>
        {translations.gameSettings.backdoLaunch[language]}
        <meshStandardMaterial color='orange'/>
      </Text3D>
      <group 
      name='setting-0-toggle' 
      position={[ruleToggleX, 0.1, -0.55]}>
        <group name='setting-0-toggle-background'>
          <mesh 
          name='setting-0-toggle-background-left-circle'
          scale={[0.25, 0.01, 0.25]}
          >
            <cylinderGeometry args={[1, 1, 1, 32]}/>
            <AnimatedMeshDistortMaterial
              speed={5}
              distort={0}
              color={setting0ToggleBackgroundColor}
            />
          </mesh>
          <mesh
          name='setting-0-toggle-background-block'
          position={[0.2, 0, 0]}
          scale={[0.4, 0.01, 0.5]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <AnimatedMeshDistortMaterial
              speed={5}
              distort={0}
              color={setting0ToggleBackgroundColor}
            />
          </mesh>
          <mesh 
          name='setting-0-toggle-background-right-circle'
          position={[0.4, 0, 0]}
          scale={[0.25, 0.01, 0.25]}
          >
            <cylinderGeometry args={[1, 1, 1, 32]}/>
            <AnimatedMeshDistortMaterial
              speed={5}
              distort={0}
              color={setting0ToggleBackgroundColor}
            />
          </mesh>
        </group>
        <animated.mesh
        name='setting-0-toggle-switch'
        scale={[0.15, 0.02, 0.15]}
        position={setting0TogglePosition}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={MeshColors.disabledGreyBackground} />
        </animated.mesh>
      </group>
      <Text3D 
      name='setting-0-description'
      font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
      position={[-0.1,ruleTextElevation,0.25]}
      rotation={[-Math.PI/2,0,0]}
      size={descriptionSize}
      height={0.01}
      lineHeight={0.8}>
        {translations.gameSettings.backdoLaunchDescription[language]}
        <meshStandardMaterial color='orange' transparent opacity={0.6}/>
      </Text3D>
    </group>
  }
  function Timer({ position }) {
    const audioVolume = useAtomValue(audioVolumeAtom)
    const [setting1Hover, setSetting1Hover] = useState(false)

    function handleSetting1PointerEnter(e) {
      e.stopPropagation()
      if (isHost && gamePhase === 'lobby') {
        document.body.style.cursor = 'pointer'
        setSetting1Hover(true)
      }
    }
    function handleSetting1PointerLeave(e) {
      e.stopPropagation()
      if (isHost && gamePhase === 'lobby') {
        document.body.style.cursor = 'default'
        setSetting1Hover(false)
      }
    }
    function handleSetting1PointerDown(e) {
      e.stopPropagation()
      if (isHost && gamePhase === 'lobby') {
        trackRuleToggle('timer', !timer)
        if (!timer) {
          socket.emit('setGameRule', ({ roomId: params.id.toUpperCase(), clientId: client._id, rule: 'timer', flag: true }))
        } else {
          socket.emit('setGameRule', ({ roomId: params.id.toUpperCase(), clientId: client._id, rule: 'timer', flag: false }))
        }
      }
      playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
    }
    return <group name='setting-1' position={position}>
      <group name='setting-1-background'>
        { isHost && gamePhase === 'lobby' && <mesh
        name='setting-1-background-outer'
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.05, 1.5]}>
          <boxGeometry args={[ruleWidth, 1, 1]}/>
          <meshStandardMaterial color='orange'/>
        </mesh> }
        <mesh 
        name='setting-1-background-inner'
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.1, 1.45]}>
          <boxGeometry args={[ruleCardInnerWidth, 1, 1]}/>
          <meshStandardMaterial 
          color={ !setting1Hover ? MeshColors.disabledGreyBackground : '#444444' } 
          transparent 
          opacity={1}/>
        </mesh>
        <mesh 
        name='setting-1-background-wrapper'
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.1, 1.5]}
        onPointerEnter={e=>handleSetting1PointerEnter(e)}
        onPointerLeave={e=>handleSetting1PointerLeave(e)}
        onPointerDown={e=>handleSetting1PointerDown(e)}>
          <boxGeometry args={[ruleWidth, 1, 1]}/>
          <meshStandardMaterial 
          transparent 
          opacity={0}/>
        </mesh>
      </group>
      <Text3D
      name='setting-1-title'
      font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
      position={[-0.1,0.1,-0.1]}
      rotation={[-Math.PI/2,0,0]}
      size={titleSize}
      height={0.01}>
        {translations.gameSettings.timer[language]}
        <meshStandardMaterial color='orange'/>
      </Text3D>
      <group 
      name='setting-1-toggle' 
      position={[ruleToggleX, 0.1, -0.3]}>
        <group name='setting-1-toggle-background'>
          <mesh 
          name='setting-1-toggle-background-left-circle'
          scale={[0.25, 0.01, 0.25]}
          >
            <cylinderGeometry args={[1, 1, 1, 32]}/>
            <AnimatedMeshDistortMaterial
              speed={5}
              distort={0}
              color={setting1ToggleBackgroundColor}
            />
          </mesh>
          <mesh
          name='setting-1-toggle-background-block'
          position={[0.2, 0, 0]}
          scale={[0.4, 0.01, 0.5]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <AnimatedMeshDistortMaterial
              speed={5}
              distort={0}
              color={setting1ToggleBackgroundColor}
            />
          </mesh>
          <mesh 
          name='setting-1-toggle-background-right-circle'
          position={[0.4, 0, 0]}
          scale={[0.25, 0.01, 0.25]}
          >
            <cylinderGeometry args={[1, 1, 1, 32]}/>
            <AnimatedMeshDistortMaterial
              speed={5}
              distort={0}
              color={setting1ToggleBackgroundColor}
            />
          </mesh>
        </group>
        <animated.mesh
        name='setting-1-toggle-switch'
        scale={[0.15, 0.02, 0.15]}
        position={setting1TogglePosition}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={MeshColors.disabledGreyBackground} />
        </animated.mesh>
      </group>
      <Text3D 
      name='setting-1-description'
      font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
      position={[-0.1,ruleTextElevation,0.5]}
      rotation={[-Math.PI/2,0,0]}
      size={descriptionSize}
      height={0.01}
      lineHeight={0.8}>
        {translations.gameSettings.timerDescription[language]}
        <meshStandardMaterial color='orange' transparent opacity={0.6}/>
      </Text3D>
    </group>
  }
  function NakThrow({ position }) {
    const audioVolume = useAtomValue(audioVolumeAtom)
    const [setting2Hover, setSetting2Hover] = useState(false)

    function handleSetting2PointerEnter(e) {
      e.stopPropagation()
      if (isHost && gamePhase === 'lobby') {
        document.body.style.cursor = 'pointer'
        setSetting2Hover(true)
      }
    }
    function handleSetting2PointerLeave(e) {
      e.stopPropagation()
      if (isHost && gamePhase === 'lobby') {
        document.body.style.cursor = 'default'
        setSetting2Hover(false)
      }
    }
    function handleSetting2PointerDown(e) {
      e.stopPropagation()
      if (isHost && gamePhase === 'lobby') {
        trackRuleToggle('nak', !nak)
        if (!nak) {
          socket.emit('setGameRule', ({ roomId: params.id.toUpperCase(), clientId: client._id, rule: 'nak', flag: true }))
        } else {
          socket.emit('setGameRule', ({ roomId: params.id.toUpperCase(), clientId: client._id, rule: 'nak', flag: false }))
        }
      }
      playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
    }
    
    return <group name='setting-2' position={position}>
      <group name='setting-2-background'>
        { isHost && gamePhase === 'lobby' && <mesh
        name='setting-2-background-outer'
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.05, 2]}>
          <boxGeometry args={[ruleWidth, 1, 1]}/>
          <meshStandardMaterial color='orange'/>
        </mesh> }
        <mesh 
        name='setting-2-background-inner'
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.1, 1.95]}>
          <boxGeometry args={[ruleCardInnerWidth, 1, 1]}/>
          <meshStandardMaterial 
          color={ !setting2Hover ? MeshColors.disabledGreyBackground : '#444444' } 
          transparent 
          opacity={1}/>
        </mesh>
        <mesh 
        name='setting-2-background-wrapper'
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.05, 2]}
        onPointerEnter={e=>handleSetting2PointerEnter(e)}
        onPointerLeave={e=>handleSetting2PointerLeave(e)}
        onPointerDown={e=>handleSetting2PointerDown(e)}>
          <boxGeometry args={[ruleWidth, 1, 1]}/>
          <meshStandardMaterial 
          transparent 
          opacity={0}/>
        </mesh>
      </group>
      <Text3D 
      name='setting-2-title'
      font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
      position={[-0.1,0.1,-0.35]}
      rotation={[-Math.PI/2,0,0]}
      size={titleSize}
      height={0.01}>
        {translations.gameSettings.nakThrow[language]}
        <meshStandardMaterial color='orange'/>
      </Text3D>
      <group 
      name='setting-2-toggle' 
      position={[ruleToggleX, 0.1, -0.55]}>
        <group name='setting-2-toggle-background'>
          <mesh 
          name='setting-2-toggle-background-left-circle'
          scale={[0.25, 0.01, 0.25]}
          >
            <cylinderGeometry args={[1, 1, 1, 32]}/>
            <AnimatedMeshDistortMaterial
              speed={5}
              distort={0}
              color={setting2ToggleBackgroundColor}
            />
          </mesh>
          <mesh
          name='setting-2-toggle-background-block'
          position={[0.2, 0, 0]}
          scale={[0.4, 0.01, 0.5]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <AnimatedMeshDistortMaterial
              speed={5}
              distort={0}
              color={setting2ToggleBackgroundColor}
            />
          </mesh>
          <mesh 
          name='setting-2-toggle-background-right-circle'
          position={[0.4, 0, 0]}
          scale={[0.25, 0.01, 0.25]}
          >
            <cylinderGeometry args={[1, 1, 1, 32]}/>
            <AnimatedMeshDistortMaterial
              speed={5}
              distort={0}
              color={setting2ToggleBackgroundColor}
            />
          </mesh>
        </group>
        <animated.mesh
        name='setting-2-toggle-switch'
        scale={[0.15, 0.02, 0.15]}
        position={setting2TogglePosition}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={MeshColors.disabledGreyBackground} />
        </animated.mesh>
      </group>
      <Text3D 
      name='setting-2-description'
      font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
      position={[-0.1,ruleTextElevation,0.25]}
      rotation={[-Math.PI/2,0,0]}
      size={descriptionSize}
      height={0.01}
      lineHeight={0.8}>
        {translations.gameSettings.nakThrowDescription[language]}
        <meshStandardMaterial color='orange' transparent opacity={0.6}/>
      </Text3D>
    </group>
  }
  function BonusThrowCatch({ position }) {
    const audioVolume = useAtomValue(audioVolumeAtom)
    const [setting3Hover, setSetting3Hover] = useState(false)

    function handleSetting3PointerEnter(e) {
      e.stopPropagation()
      if (isHost && gamePhase === 'lobby') {
        document.body.style.cursor = 'pointer'
        setSetting3Hover(true)
      }
    }
    function handleSetting3PointerLeave(e) {
      e.stopPropagation()
      if (isHost && gamePhase === 'lobby') {
        document.body.style.cursor = 'default'
        setSetting3Hover(false)
      }
    }
    function handleSetting3PointerDown(e) {
      e.stopPropagation()
      if (isHost && gamePhase === 'lobby') {
        trackRuleToggle('yut_mo_catch', !yutMoCatch)
        if (!yutMoCatch) {
          socket.emit('setGameRule', ({ roomId: params.id.toUpperCase(), clientId: client._id, rule: 'yutMoCatch', flag: true }))
        } else {
          socket.emit('setGameRule', ({ roomId: params.id.toUpperCase(), clientId: client._id, rule: 'yutMoCatch', flag: false }))
        }
      }
      playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
    }
    
    return <group name='setting-3' position={position}>
      <group name='setting-3-background'>
        { isHost && gamePhase === 'lobby' && <mesh
        name='setting-3-background-outer'
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.05, 2]}>
          <boxGeometry args={[ruleWidth, 1, 1]}/>
          <meshStandardMaterial color='orange'/>
        </mesh> }
        <mesh 
        name='setting-3-background-inner'
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.1, 1.95]}>
          <boxGeometry args={[ruleCardInnerWidth, 1, 1]}/>
          <meshStandardMaterial 
          color={ !setting3Hover ? MeshColors.disabledGreyBackground : '#444444' } 
          transparent 
          opacity={1}/>
        </mesh>
        <mesh 
        name='setting-3-background-wrapper'
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.1, 2]}
        onPointerEnter={e=>handleSetting3PointerEnter(e)}
        onPointerLeave={e=>handleSetting3PointerLeave(e)}
        onPointerDown={e=>handleSetting3PointerDown(e)}>
          <boxGeometry args={[ruleWidth, 1, 1]}/>
          <meshStandardMaterial 
          transparent 
          opacity={0}/>
        </mesh>
      </group>
      <Text3D 
      name='setting-3-title'
      font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
      position={[-0.1,0.1,-0.35]}
      rotation={[-Math.PI/2,0,0]}
      size={titleSize}
      height={0.01}>
        {translations.gameSettings.bonusThrowCatch[language]}
        <meshStandardMaterial color='orange'/>
      </Text3D>
      <group 
      name='setting-3-toggle' 
      position={[ruleToggleX, 0.1, -0.55]}>
        <group name='setting-3-toggle-background'>
          <mesh 
          name='setting-3-toggle-background-left-circle'
          scale={[0.25, 0.01, 0.25]}
          >
            <cylinderGeometry args={[1, 1, 1, 32]}/>
            <AnimatedMeshDistortMaterial
              speed={5}
              distort={0}
              color={setting3ToggleBackgroundColor}
            />
          </mesh>
          <mesh
          name='setting-3-toggle-background-block'
          position={[0.2, 0, 0]}
          scale={[0.4, 0.01, 0.5]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <AnimatedMeshDistortMaterial
              speed={5}
              distort={0}
              color={setting3ToggleBackgroundColor}
            />
          </mesh>
          <mesh 
          name='setting-3-toggle-background-right-circle'
          position={[0.4, 0, 0]}
          scale={[0.25, 0.01, 0.25]}
          >
            <cylinderGeometry args={[1, 1, 1, 32]}/>
            <AnimatedMeshDistortMaterial
              speed={5}
              distort={0}
              color={setting3ToggleBackgroundColor}
            />
          </mesh>
        </group>
        <animated.mesh
        name='setting-3-toggle-switch'
        scale={[0.15, 0.02, 0.15]}
        position={setting3TogglePosition}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={MeshColors.disabledGreyBackground} />
        </animated.mesh>
      </group>
      <Text3D 
      name='setting-3-description'
      font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
      position={[-0.1,ruleTextElevation,0.25]}
      rotation={[-Math.PI/2,0,0]}
      size={descriptionSize}
      height={0.01}
      lineHeight={0.8}>
        {translations.gameSettings.bonusThrowCatchDescription[language]}
        <meshStandardMaterial color='orange' transparent opacity={0.6}/>
      </Text3D>
    </group>
  }
  function SplitPaths({ position }) {
    const audioVolume = useAtomValue(audioVolumeAtom)
    const [setting4Hover, setSetting4Hover] = useState(false)

    function handleSetting4PointerEnter(e) {
      e.stopPropagation()
      if (isHost && gamePhase === 'lobby') {
        document.body.style.cursor = 'pointer'
        setSetting4Hover(true)
      }
    }
    function handleSetting4PointerLeave(e) {
      e.stopPropagation()
      if (isHost && gamePhase === 'lobby') {
        document.body.style.cursor = 'default'
        setSetting4Hover(false)
      }
    }
    function handleSetting4PointerDown(e) {
      e.stopPropagation()
      if (isHost && gamePhase === 'lobby') {
        trackRuleToggle('shortcut_options', !shortcutOptions)
        if (!shortcutOptions) {
          socket.emit('setGameRule', ({ roomId: params.id.toUpperCase(), clientId: client._id, rule: 'shortcutOptions', flag: true }))
        } else {
          socket.emit('setGameRule', ({ roomId: params.id.toUpperCase(), clientId: client._id, rule: 'shortcutOptions', flag: false }))
        }
      }
      playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
    }
    return <group name='setting-4' position={position}>
      <group name='setting-4-background'>
        { isHost && gamePhase === 'lobby' && <mesh
        name='setting-4-background-outer'
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.05, 2]}>
          <boxGeometry args={[ruleWidth, 1, 1]}/>
          <meshStandardMaterial color='orange'/>
        </mesh> }
        <mesh 
        name='setting-4-background-inner'
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.1, 1.95]}>
          <boxGeometry args={[ruleCardInnerWidth, 1, 1]}/>
          <meshStandardMaterial 
          color={ !setting4Hover ? MeshColors.disabledGreyBackground : '#444444' } 
          transparent 
          opacity={1}/>
        </mesh>
        <mesh 
        name='setting-4-background-wrapper'
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.1, 2]}
        onPointerEnter={e=>handleSetting4PointerEnter(e)}
        onPointerLeave={e=>handleSetting4PointerLeave(e)}
        onPointerDown={e=>handleSetting4PointerDown(e)}>
          <boxGeometry args={[ruleWidth, 1, 1]}/>
          <meshStandardMaterial 
          transparent 
          opacity={0}/>
        </mesh>
      </group>
      <Text3D 
      name='setting-4-title'
      font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
      position={[-0.1,0.1,-0.35]}
      rotation={[-Math.PI/2,0,0]}
      size={titleSize}
      height={0.01}>
        {translations.gameSettings.splitPaths[language]}
        <meshStandardMaterial color='orange'/>
      </Text3D>
      <group 
      name='setting-4-toggle' 
      position={[ruleToggleX, 0.1, -0.55]}>
        <group name='setting-4-toggle-background'>
          <mesh 
          name='setting-4-toggle-background-left-circle'
          scale={[0.25, 0.01, 0.25]}
          >
            <cylinderGeometry args={[1, 1, 1, 32]}/>
            <AnimatedMeshDistortMaterial
              speed={5}
              distort={0}
              color={setting4ToggleBackgroundColor}
            />
          </mesh>
          <mesh
          name='setting-4-toggle-background-block'
          position={[0.2, 0, 0]}
          scale={[0.4, 0.01, 0.5]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <AnimatedMeshDistortMaterial
              speed={5}
              distort={0}
              color={setting4ToggleBackgroundColor}
            />
          </mesh>
          <mesh 
          name='setting-4-toggle-background-right-circle'
          position={[0.4, 0, 0]}
          scale={[0.25, 0.01, 0.25]}
          >
            <cylinderGeometry args={[1, 1, 1, 32]}/>
            <AnimatedMeshDistortMaterial
              speed={5}
              distort={0}
              color={setting4ToggleBackgroundColor}
            />
          </mesh>
        </group>
        <animated.mesh
        name='setting-4-toggle-switch'
        scale={[0.15, 0.02, 0.15]}
        position={setting4TogglePosition}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={MeshColors.disabledGreyBackground} />
        </animated.mesh>
      </group>
      <Text3D 
      name='setting-4-description'
      font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
      position={[-0.1,ruleTextElevation,0.25]}
      rotation={[-Math.PI/2,0,0]}
      size={descriptionSize}
      height={0.01}
      lineHeight={0.8}>
        {translations.gameSettings.splitPathsDescription[language]}
        <meshStandardMaterial color='orange' transparent opacity={0.6}/>
      </Text3D>
    </group>
  }

  function MinigameSetting({ position }) {
    const [hover, setHover] = useState(false)

    function toggle(event) {
      event.stopPropagation()
      console.log('toggle minigame rule')
      if (isHost && gamePhase === 'lobby') {
        trackRuleToggle('minigame', !minigameRule)
        socket.emit('setGameRule', {
          roomId: params.id.toUpperCase(),
          clientId: client._id,
          rule: 'minigame',
          flag: !minigameRule
        })
      }
    }

    return <group name='minigame-setting' position={position}>
      {isHost && gamePhase === 'lobby' && <mesh position={[ruleCardCenter, 0, 0]} scale={[1, 0.05, 1.5]}><boxGeometry args={[ruleWidth, 1, 1]}/><meshStandardMaterial color='orange'/></mesh>}
      <mesh position={[ruleCardCenter, 0, 0]} scale={[1, 0.1, 1.45]}><boxGeometry args={[ruleCardInnerWidth, 1, 1]}/><meshStandardMaterial color={hover ? '#444444' : MeshColors.disabledGreyBackground}/></mesh>
      <mesh
        position={[ruleCardCenter, 0, 0]}
        scale={[1, 0.1, 1.5]}
        onPointerEnter={(event) => { event.stopPropagation(); if (isHost && gamePhase === 'lobby') { setHover(true); document.body.style.cursor = 'pointer' } }}
        onPointerLeave={(event) => { event.stopPropagation(); setHover(false); document.body.style.cursor = 'default' }}
        onPointerDown={toggle}
      ><boxGeometry args={[ruleWidth, 1, 1]}/><meshStandardMaterial transparent opacity={0}/></mesh>
      <Text3D font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"} position={[-0.1, 0.1, -0.1]} rotation={[-Math.PI / 2, 0, 0]} size={titleSize} height={0.01}>{translations.gameSettings.minigame[language]}<meshStandardMaterial color='orange'/></Text3D>
      <Text3D font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"} position={[-0.1, ruleTextElevation, 0.5]} rotation={[-Math.PI / 2, 0, 0]} size={descriptionSize} height={0.01} lineHeight={0.8}>{translations.gameSettings.minigameDescription[language]}<meshStandardMaterial color='orange' transparent opacity={0.6}/></Text3D>
      <group position={[ruleToggleX, 0.1, -0.3]}>
        <mesh scale={[0.25, 0.01, 0.25]}><cylinderGeometry args={[1, 1, 1, 32]}/><AnimatedMeshDistortMaterial speed={5} distort={0} color={minigameToggleBackgroundColor}/></mesh>
        <mesh position={[0.2, 0, 0]} scale={[0.4, 0.01, 0.5]}><boxGeometry args={[1, 1, 1]}/><AnimatedMeshDistortMaterial speed={5} distort={0} color={minigameToggleBackgroundColor}/></mesh>
        <mesh position={[0.4, 0, 0]} scale={[0.25, 0.01, 0.25]}><cylinderGeometry args={[1, 1, 1, 32]}/><AnimatedMeshDistortMaterial speed={5} distort={0} color={minigameToggleBackgroundColor}/></mesh>
        <animated.mesh scale={[0.15, 0.02, 0.15]} position={minigameTogglePosition}><cylinderGeometry args={[1, 1, 1, 32]}/><meshStandardMaterial color={MeshColors.disabledGreyBackground}/></animated.mesh>
      </group>
    </group>
  }

  return <group position={position} scale={scale}>
    <Timer position={[5.2, 0, -3.7]} />
    <NakThrow position={[5.2, 0, -1.85]} />
    <BackdoLaunch position={[5.2, 0, 0.25]} />
    <BonusThrowCatch position={[5.2, 0, 2.35]} />
    <SplitPaths position={[5.2, 0, 4.45]} />
    <MinigameSetting position={[5.2, 0, 6.3]} />
    {showMainMenu && <BackToMainMenuButton
      position={layout[device].lobby.backToMainMenuButton.position}
      width={layout[device].lobby.backToMainMenuButton.width}
    />}
  </group>
}
//-3.2
//-3.2+1.85
//-3.2+1.85+2.1